import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatMoney, type CatalogItem, type Offer, type Store, type TripSummary } from '@sp/shared'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { errorMessage, invalidateResources } from '../lib/useResource.ts'
import { addOffersToTrip, applyOfferToTrips, offerPlace, openTrips, type OfferPlace } from '../lib/offerToTrip.ts'
import {
  cardNote,
  chainLabel,
  offerOriginal,
  offerPrice,
  offerTitle,
  offerValidity,
  variantNote,
} from '../lib/offers.ts'
import { formatShortWeekday, intoStore, itemsCount, tripName } from '../lib/format.ts'
import { Sheet } from './Sheet.tsx'
import { Icon } from './Icon.tsx'
import { OfferImage } from './OfferImage.tsx'
import { Button, Divider, Label, Paragraph, SelectField, SelectRow, TextCol } from './ui.tsx'

interface AddTarget {
  trip: TripSummary
  place: OfferPlace
}

/** Rozpracovane nakupy, na ktere akce muze jit — i kdyz obchod na trase jeste neni (prida se). */
function addTargets(offer: Offer, trips: readonly TripSummary[], stores: readonly Store[]): AddTarget[] {
  return openTrips(trips).flatMap((trip) => {
    const place = offerPlace(offer, trip, stores)
    return place ? [{ trip, place }] : []
  })
}

export function OfferSheet({
  offer,
  onClose,
  catalogItem = null,
  onUsePrice,
  addTo,
  linkTo,
}: {
  offer: Offer | null
  onClose(): void
  /** Polozka sablony, ke ktere akce patri. */
  catalogItem?: CatalogItem | null | undefined
  /** V planovani a vyberu: prevzit akcni cenu. */
  onUsePrice?: (() => void) | undefined
  /** Na strance Akce: pridat na rozpracovany nakup. */
  addTo?: { trips: readonly TripSummary[]; stores: readonly Store[] } | undefined
  /** Na strance Akce: zapamatovat akci u polozky sablony. */
  linkTo?: { catalog: readonly CatalogItem[]; onLinked(item: CatalogItem): void } | undefined
}) {
  return (
    <Sheet
      open={offer !== null}
      onClose={onClose}
      title={offer ? offerTitle(offer) : ''}
      subtitle={offer ? [chainLabel(offer.chain), offer.leafletName].filter(Boolean).join(' · ') : undefined}
    >
      {offer && (
        <OfferDetail
          key={offer.id}
          offer={offer}
          catalogItem={catalogItem}
          onUsePrice={onUsePrice}
          addTo={addTo}
          linkTo={linkTo}
        />
      )}
    </Sheet>
  )
}

function OfferDetail({
  offer,
  catalogItem,
  onUsePrice,
  addTo,
  linkTo,
}: {
  offer: Offer
  catalogItem: CatalogItem | null
  onUsePrice: (() => void) | undefined
  addTo: { trips: readonly TripSummary[]; stores: readonly Store[] } | undefined
  linkTo: { catalog: readonly CatalogItem[]; onLinked(item: CatalogItem): void } | undefined
}) {
  const navigate = useNavigate()
  const [added, setAdded] = useState<Set<string>>(() => new Set())
  const [linkId, setLinkId] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const price = offerPrice(offer)
  const old = offer.oldPriceMinor !== null ? formatMoney(offer.oldPriceMinor, offer.currency) : null
  const meta = [offer.brand, offer.unitPrice, offerValidity(offer)].filter(Boolean).join(' · ')
  const note = cardNote(offer)
  const original = offerOriginal(offer)
  const variant = variantNote(offer)
  const targets = addTo ? addTargets(offer, addTo.trips, addTo.stores) : []
  const chainStore = addTo?.stores.find((s) => !s.archived && s.offerChain === offer.chain) ?? null

  async function run(action: () => Promise<string>) {
    setBusy(true)
    setMessage(null)
    try {
      setMessage({ ok: true, text: await action() })
    } catch (err) {
      setMessage({
        ok: false,
        text: err instanceof HttpError && err.isOffline ? 'Tohle jde jen se signálem.' : errorMessage(err),
      })
    } finally {
      setBusy(false)
    }
  }

  function addToTrip(target: AddTarget) {
    void run(async () => {
      const result = await addOffersToTrip(target.trip.id, [{ offer, catalogItem }], addTo?.stores ?? [])
      setAdded((prev) => new Set(prev).add(target.trip.id))
      if (addTo) invalidateResources(`sp:nakupy:${target.trip.groupId}`)
      const moved = result.routeAdded.length > 0 ? ` ${target.place.store?.name ?? ''} přibyl na trasu.` : ''
      const [bought] = result.inBasket
      if (bought) return `${bought.name} už je na nákupu v košíku.`
      const [updated] = result.updated
      if (updated) {
        const withPrice = offer.priceMinor !== null ? ' s cenou z akce' : ''
        return target.place.store && updated.storeId !== target.place.store.id
          ? `${updated.name} už na nákupu byla – přesunuta ${intoStore(target.place.store.name)}${withPrice}.${moved}`
          : `${updated.name} už na nákupu je${withPrice ? ' – cena je teď z akce' : ''}.${moved}`
      }
      return `Přidáno: ${tripName(target.trip, addTo?.stores ?? []).title}.${moved}`
    })
  }

  /** Zadny nakup s timhle obchodem — zalozi se novy rovnou s obchodem akce. */
  function newTrip(store: Store, groupId: string) {
    void run(async () => {
      const created = await api.trips.create(groupId, { kind: 'quick', storeIds: [store.id] })
      await addOffersToTrip(created.id, [{ offer, catalogItem }], addTo?.stores ?? [])
      invalidateResources(`sp:nakupy:${groupId}`)
      navigate(`/nakup/${created.id}`)
      return `Založen nákup ${intoStore(store.name)}.`
    })
  }

  function link() {
    const item = linkTo?.catalog.find((c) => c.id === linkId)
    if (!item || !linkTo) return
    void run(async () => {
      const terms = [item.offerTerms, offer.name].filter(Boolean).join(', ').slice(0, 300)
      const updated = await api.catalog.update(item.id, { offerTerms: terms })
      linkTo.onLinked(updated)
      setLinkId('')
      // A rovnou k polozce na nakupu, kde uz je — kvuli tomu se sem clovek proklikal.
      const applied = addTo ? await applyOfferToTrips(offer, updated, addTo.trips, addTo.stores) : []
      if (applied.length > 0 && addTo) invalidateResources(`sp:nakupy:${applied[0]?.groupId ?? ''}`)
      const where = applied.map((t) => tripName(t, addTo?.stores ?? []).title).join(', ')
      return applied.length > 0
        ? `${updated.name} na nákupu ${where} je teď v akci. Příště se tahle akce u položky najde sama.`
        : `U položky ${updated.name} se bude tahle akce hledat.`
    })
  }

  return (
    <div className="stack">
      <OfferImage offer={offer} size="sheet" />
      <div className="stack-4">
        <div className="row wrap-row">
          <span className="offer-price-big">{price ?? 'Cena v letáku'}</span>
          {old && <span className="offer-old">{old}</span>}
        </div>
        {original && <Paragraph size="meta" tone="fg">{`V letáku: ${original}`}</Paragraph>}
        {meta && <Paragraph size="meta">{meta}</Paragraph>}
        {variant && <Paragraph size="meta">{variant}</Paragraph>}
        {offer.description && <Paragraph>{offer.description}</Paragraph>}
        {note && <Paragraph size="meta" className="offer-note">{note}</Paragraph>}
        {catalogItem && <Paragraph size="meta" tone="fg">{`Ze šablony: ${catalogItem.name}`}</Paragraph>}
      </div>

      {onUsePrice && offer.priceMinor !== null && (
        <Button kind="primary" full onClick={onUsePrice}>
          Použít cenu z akce
        </Button>
      )}
      {offer.url && (
        <a className="btn btn-outlined btn-full" href={offer.url} target="_blank" rel="noopener noreferrer">
          <Icon name="link" />
          <span className="btn-label">Otevřít leták</span>
        </a>
      )}

      {addTo && (
        <>
          <Divider />
          <Label as="span">Přidat na nákup</Label>
          {targets.map((target) => (
            <button
              key={target.trip.id}
              type="button"
              className="select-row select-row-pad-even"
              disabled={busy || added.has(target.trip.id)}
              onClick={() => addToTrip(target)}
            >
              <TextCol
                title={tripName(target.trip, addTo?.stores ?? []).title}
                sub={[
                  target.trip.plannedDate ? formatShortWeekday(target.trip.plannedDate) : null,
                  target.place.store
                    ? target.place.addsStore
                      ? `přidá ${target.place.store.name} na trasu`
                      : target.place.store.name
                    : 'kdekoli',
                  itemsCount(target.trip.itemCount),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
              <Icon name={added.has(target.trip.id) ? 'check' : 'plus'} size={20} />
            </button>
          ))}
          {chainStore && (
            <button
              type="button"
              className="select-row select-row-pad-even"
              disabled={busy}
              onClick={() => newTrip(chainStore, chainStore.groupId)}
            >
              <TextCol title="Nový nákup" sub={`${chainStore.name} · založí se s touhle akcí`} />
              <Icon name="plus" size={20} />
            </button>
          )}
          {targets.length === 0 && !chainStore && (
            <Paragraph size="meta">Skupina nemá obchod, kde tahle akce platí.</Paragraph>
          )}
        </>
      )}

      {linkTo && linkTo.catalog.length > 0 && !catalogItem && (
        <>
          <Divider />
          <div className="stack-8">
            <Label as="span">Patří k mé položce</Label>
            <SelectField
              ariaLabel="Položka šablony"
              placeholder="Vyber položku"
              value={linkId}
              onChange={setLinkId}
              options={[...linkTo.catalog]
                .sort((a, b) => a.name.localeCompare(b.name, 'cs'))
                .map((c) => ({ value: c.id, label: c.name }))}
            />
            <Button className="self-start" disabled={!linkId || busy} onClick={link}>
              Potvrdit
            </Button>
            <Paragraph size="meta">
              Akce se použije u položky na rozpracovaném nákupu a název z letáku se k ní uloží – příště se najde sama.
            </Paragraph>
          </div>
        </>
      )}

      {message && <Paragraph tone={message.ok ? 'fg' : 'error'}>{message.text}</Paragraph>}
    </div>
  )
}
